import { groupStateHasWork } from '../groups/model'
import { readJson, removeKey, writeJson } from './storage'

// Drafts of the New analysis panel: a short list per user, newest first, kept in this
// browser (localStorage). Every panel session has its own draft id and writes only its
// own entry, so one session never overwrites another. File objects are not stored: a
// draft holds the workflow, the batch name, the samples (file ids and metadata) and
// the group flows' state (multisample groups, cohort members, carrier couples), which
// is enough to continue later. groupState belongs to the group flows (groups/model.js)
// and is stored as it is.
//
// draft = { id, workflow, samples, name, groupState, savedAt }

export const MAX_DRAFTS = 5

const DRAFTS_KEY = 'genesus.newAnalysis.drafts'
// Before the list there was one draft per user, and before that one shared by every
// user. Both move into the list on its first read (a failed write keeps them for the
// next read) and are then removed.
const LEGACY_DRAFT_KEY = 'genesus.newAnalysis.draft'
const MIGRATED = [
  { key: (userKey) => `${LEGACY_DRAFT_KEY}.${userKey || 'anonymous'}`, id: 'earlier-draft' },
  { key: () => LEGACY_DRAFT_KEY, id: 'earlier-shared-draft' },
]

export const draftsKey = (userKey) => `${DRAFTS_KEY}.${userKey || 'anonymous'}`

let idCounter = 0
/** A new draft id, one per panel session. */
export const newDraftId = () => {
  idCounter += 1
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}-${idCounter}`
}

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const toDraft = (raw, fallbackId) => {
  if (!isObject(raw) || !Array.isArray(raw.samples)) return null
  const id = typeof raw.id === 'string' && raw.id ? raw.id : fallbackId
  if (!id) return null
  return {
    id,
    workflow: isObject(raw.workflow) ? raw.workflow : null,
    samples: raw.samples,
    name: typeof raw.name === 'string' ? raw.name : '',
    groupState: isObject(raw.groupState) ? raw.groupState : null,
    savedAt: typeof raw.savedAt === 'number' ? raw.savedAt : 0,
  }
}

/** A draft worth keeping: file samples, or group work (groups, cohort members, couples). */
export const draftHasWork = (draft) =>
  Boolean(draft && ((Array.isArray(draft.samples) && draft.samples.length) || groupStateHasWork(draft.groupState)))

const newestFirst = (a, b) => b.savedAt - a.savedAt

// Sorted newest first and capped: the oldest drafts go first.
const tidy = (list) => [...list].sort(newestFirst).slice(0, MAX_DRAFTS)

const writeList = (list, userKey) => {
  const kept = tidy(list)
  if (kept.length) return writeJson(draftsKey(userKey), kept)
  removeKey(draftsKey(userKey))
  return true
}

const readDrafts = (userKey) => {
  const raw = readJson(draftsKey(userKey))
  const seen = new Set()
  const list = []
  ;(Array.isArray(raw) ? raw : []).forEach((item) => {
    const draft = toDraft(item)
    if (!draft || seen.has(draft.id)) return
    seen.add(draft.id)
    list.push(draft)
  })
  // A tab still on the earlier version can write the old key again after the move: a
  // newer copy replaces the moved one, an older one (or the same) is left out.
  let moved = false
  MIGRATED.forEach(({ key, id }) => {
    const draft = toDraft(readJson(key(userKey)), id)
    if (!draftHasWork(draft)) return
    const at = list.findIndex((d) => d.id === draft.id)
    if (at === -1) list.push(draft)
    else if (draft.savedAt > list[at].savedAt) list[at] = draft
    else return
    moved = true
  })
  if (moved && !writeList(list, userKey)) return tidy(list)
  MIGRATED.forEach(({ key }) => removeKey(key(userKey)))
  return tidy(list)
}

/** The user's drafts, newest first (at most MAX_DRAFTS). */
export const loadDrafts = (userKey) => readDrafts(userKey)

/** One of the user's drafts, or null. */
export const loadDraft = (id, userKey) => (id ? readDrafts(userKey).find((d) => d.id === id) || null : null)

/**
 * Saves one draft, by its id, as the user's newest; the other drafts stay as they are
 * (past MAX_DRAFTS the oldest goes). False when storage fails.
 */
export const saveDraft = ({ id, workflow, samples, name, groupState } = {}, userKey) => {
  if (!id) return false
  const draft = {
    id,
    workflow: workflow ?? null,
    samples: Array.isArray(samples) ? samples : [],
    name: name ?? '',
    groupState: groupState ?? null,
    savedAt: Date.now(),
  }
  return writeList([draft, ...readDrafts(userKey).filter((d) => d.id !== id)], userKey)
}

/** Removes one of the user's drafts. Returns it, for an undo, or null. */
export const deleteDraft = (id, userKey) => {
  const list = readDrafts(userKey)
  const draft = list.find((d) => d.id === id) || null
  if (draft)
    writeList(
      list.filter((d) => d.id !== id),
      userKey,
    )
  return draft
}

/**
 * Puts a deleted draft back as it was, in its place by savedAt. It never pushes another
 * draft out: false when the list has filled up again meanwhile, or storage fails.
 */
export const restoreDraft = (draft, userKey) => {
  const kept = toDraft(draft)
  if (!kept) return false
  const others = readDrafts(userKey).filter((d) => d.id !== kept.id)
  if (others.length >= MAX_DRAFTS) return false
  return writeList([...others, kept], userKey)
}

// What stays a draft after a batch start: every sample that did not start, whether
// it failed, was skipped or was never ready to send. Samples that started, and those
// whose start got no reply (they may have started), are left out. In a tumour-normal
// batch a normal stays only while a sample kept here still uses it.
// outcomeOf(uid) is the sample's last start status, or undefined when never sent.
export const notStartedSamples = (samples, outcomeOf, { tumourNormal = false } = {}) => {
  const sent = (s) => ['started', 'unknown'].includes(outcomeOf(s.uid))
  if (!tumourNormal) return samples.filter((s) => !sent(s))
  const normalIds = new Set(samples.map((s) => s.normalFileId).filter(Boolean))
  const kept = samples.filter((s) => !normalIds.has(s.fileId) && !sent(s))
  const keptNormals = new Set(kept.map((s) => s.normalFileId).filter(Boolean))
  return samples.filter((s) => kept.includes(s) || (normalIds.has(s.fileId) && keptNormals.has(s.fileId)))
}
